/**
 * Request transform for ADS Capability requests (generic for all pages)
 *
 * Takes AdsCapabilityVariables and returns a request object compatible with the query API.
 * The actual gRPC call is made by middleware that matches the query type to the appropriate client.
 *
 * This transform can be used by any page type (homepage, booking panel, combined results, etc.)
 * Page-specific hooks provide the experience, vertical and slots.
 * The pathPrefix is configured in the ADS Capability client, not in this transform.
 */

import { buildAdsCapabilityRequestBody } from '@web-platform/shared-universal-utils-universal/src/adverts/adsCapabilityRequestBody';

import type { AdsCapabilityVariables } from '@web-platform/shared-universal-types/src/network/banana/requestTypes/adsCapabilityRequestTypes';
import type { AdsCapabilityRequestInit } from '@web-platform/shared-universal-types/src/network/types';

/**
 * Transforms AdsCapabilityVariables into a request compatible with the query API.
 * Page-specific variables are passed through without defaults.
 * The request object body contains the actual data for the gRPC call.
 * The empty url and POST method are for backwards compatibility with the query API.
 * The pathPrefix is configured in the gRPC client wrapper, not here.
 * @param {AdsCapabilityVariables} variables - Page-specific variables for the request
 * @returns {AdsCapabilityRequestInit} Request object compatible with the query API
 */
export const getAdsCapabilityRequestTransform = (
  variables: AdsCapabilityVariables,
): AdsCapabilityRequestInit => ({
  body: buildAdsCapabilityRequestBody(variables),
  headers: {},
  method: 'POST',
  url: '', // Empty - pathPrefix is configured in the gRPC client
});

// Re-export with homepage-specific name for backwards compatibility
export const getAdsCapabilityHomepageRequestTransform = getAdsCapabilityRequestTransform;
